import { LuIcon, Menu, type MenuClickTriggerProps, type MenuRootProps, type MenuVariantProps } from "@overmap-ai/blocks"
import { nameModelSortFn, type ProcedureType, type Stored } from "@overmap-ai/common"
import { useSort, useTextFilter } from "@overmap-ai/hooks"
import { memo, type ReactElement } from "react"

import { procedureTypeFilterCallback } from "../../filtering/utils"
import { modelIconMapping } from "../../mappings/icons"
import {
	ProcedureTypeMultiSelectGroup,
	type ProcedureTypeMultiSelectGroupProps,
} from "../MenuMultiSelectGroups/ProcedureTypeMultiSelectGroup"

interface ProcedureTypeMultiSelectProps
	extends
		MenuRootProps,
		ProcedureTypeMultiSelectGroupProps,
		MenuVariantProps,
		Omit<MenuClickTriggerProps, "children" | "ref"> {
	children: ReactElement
}

export const ProcedureTypeMultiSelect = memo((props: ProcedureTypeMultiSelectProps) => {
	const { children, values, onValuesChange, procedureTypes, disabled, variant, size, accentColor, ...rest } = props

	const sortedProcedureTypes = useSort(procedureTypes, nameModelSortFn)

	const [filteredProcedureTypes, filterValue, setFilterValue] = useTextFilter<Stored<ProcedureType>>(
		sortedProcedureTypes,
		procedureTypeFilterCallback,
	)

	const isEmpty = filteredProcedureTypes.length === 0

	return (
		<Menu.Root {...rest}>
			<Menu.ClickTrigger disabled={disabled}>{children}</Menu.ClickTrigger>
			<Menu.Content variant={variant} size={size} accentColor={accentColor}>
				<Menu.InputRoot>
					<Menu.InputSlot>
						<LuIcon icon={modelIconMapping.search} />
					</Menu.InputSlot>
					<Menu.InputField placeholder="Search" value={filterValue} onValueChange={setFilterValue} />
				</Menu.InputRoot>
				{!isEmpty ? <Menu.Separator /> : null}
				<Menu.Scroll>
					<ProcedureTypeMultiSelectGroup
						values={values}
						procedureTypes={filteredProcedureTypes}
						onValuesChange={onValuesChange}
					/>
				</Menu.Scroll>
			</Menu.Content>
		</Menu.Root>
	)
})
ProcedureTypeMultiSelect.displayName = "ProcedureTypeMultiSelect"
